import React, { useEffect, useState } from 'react';
import { useForm} from 'react-hook-form';
import {
    TextField,
    Select,
    MenuItem,
    Button,
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    Alert
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EmailIcon from '@mui/icons-material/Email';
import FileUploadComponent from './FileUploadComponent';
import { useNavigate } from 'react-router-dom';
import { validateToken } from './utilities/validateToken';
import api from './utilities/apiInterceptor';

const CreatePaymentForm = () => {
    const token = localStorage.getItem('authToken');
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const [generatedUrl, setGeneratedUrl] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    const navigate = useNavigate();

    useEffect(() =>{
        const checkAuthentication = async () => {
            const token = localStorage.getItem('authToken');
            const isValid = await validateToken(token);
            
            if (!isValid) {
                localStorage.removeItem('authToken');
                navigate('login');
            }
        };

        checkAuthentication();
    }, [navigate]);

     const onSubmit = async (data) => {
        try {
            const response = await api.post('/create-payment', {
                'payment_type': data.type,
                'email': data.email,
                'x_amount': data.amount,
                'x_invoice_num': data.invoiceNumber,
                'x_order_num': data.orderNum,
                'x_cust_id': data.customerNumber,
                'description': data.description || '',
                'x_po_num': data.purchaseOrderNumber || '',
                'paid': false
            },
            {headers: {
                Authorization: `Bearer ${token}`,
            }}
        );

            const url = `${window.location.origin}/payment/${data.type}/${response.data}`
            setGeneratedUrl(url);

        } catch (error) {
            console.error('Error generating URL:', error);
        }
     };

     const handleReset = () => {
        reset();
        setGeneratedUrl('');
        setSelectedFile(null)
     }

     const sendEmail = async (file = null) => {
        let generatedUrlArray = generatedUrl.split('/')
        let generatedUrlLastEl = generatedUrlArray[generatedUrlArray.length - 1]
        
        let response = null;
        if (file){
            response = await api.post(`/send-email/${generatedUrlLastEl}`, {
            'pdf_invoice_attachment': selectedFile[0]
        }, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        } else {
            
            if(token) {
             response = await api.post(`/send-email/${generatedUrlLastEl}`, {}, {headers: {
                Authorization: `Bearer ${token}`,
            }})
        
        
            if (response.data.message === 'Email Sent') {
                setAlertMessage('Email sent sucessfully');
                setAlertType('success')
                }
            }
     }};

     const handleSendEmail = async () => {
        try {
            if (selectedFile){
                sendEmail(selectedFile);
            }
            else {
                sendEmail();
            }
            
        } catch(error) {
            console.log('Error sending')
        }
     }
  
    return (
        <Container>
            <Typography variant='h4' component='h1' gutterBottom>
                Create a Payment
            </Typography>
            <Typography variant='body1' gutterBottom>
                Select a payment type for the customer, enter the following information, click Generate URL to generate a URL for the customer to click on. Click Copy URL to copy the URL, click Send Email to send an email with the URL to the customer.
                </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                <Paper sx={{ padding: 2}}>
                <Box 
                    component='form' 
                    onSubmit={handleSubmit(onSubmit)} 
                    sx= {{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                        <label>Payment Type:</label>
                        <Select 
                            label='Payment Type'
                            defaultValue=''
                            {...register('type',{required: true})}
                            error={!!errors.type}
                            sx={{ minWidth: '100px'}}
                        >
                            <MenuItem value="customerpayments" >Customer Payment (Melville)</MenuItem>
                            <MenuItem value="emc_apreauth" >Pre-authorization Payment (EMC&amp;A - PL)</MenuItem>
                            <MenuItem value="emc_aother" >Freight or Additional Payment (EMC&amp;A - PL)</MenuItem>
                            <MenuItem value="nspreauth" >Training Payment (Bothell - NS)</MenuItem>
                            <MenuItem value="ns_on_train">Online Training Payment (Bothell - NS)</MenuItem>

                            <MenuItem value="sa_nonwarranty">Non Warranty Payment (EMC&amp;A - NO)</MenuItem>
                            <MenuItem value="sa_training">Training Payment (EMC&amp;A - NO)</MenuItem>
                            
                            <MenuItem value="birchwood">Customer Payment (Birchwood)</MenuItem>
                            <MenuItem value="intense">Customer Payment (Intense)</MenuItem>
                        </Select>
                        {errors.type && <Typography color='error'>A Payment Type is Required </Typography>}
                    
                    <TextField
                        label='Customer Email'
                        type='email'
                        {...register('email', { required: true})}
                        error={!!errors.email}
                        helperText={errors.email ? 'This field is required' : ''}
                    />
                    <TextField
                        label='Amount'
                        type='number'
                        {...register('amount', { required: true})}
                        helperText={errors.amount ? 'This field is required': ''}
                        inputProps={{step: '.01'}}
                        
                    />
                    
                    <TextField
                        label='Invoice Number'
                        {...register('invoiceNumber', { required: true})}
                        helperText={errors.amount ? 'This field is required': ''}
                    />
                    <TextField
                        label='Order Number'
                        {...register('orderNum', { required: true})}
                        helperText={errors.orderNum? 'A valid Order Number is required': ''}
                    />
                    <TextField
                        label='Purchase Order Number'
                        {...register('purchaseOrderNumber', {required: false})}
                        helperText={errors.orderNum? 'A valid Purchase Order Number is required': ''}
                    />
                    <TextField
                        label='Customer Number'
                        {...register('customerNumber', {required: false})}
                        helperText={errors.orderNum? 'A valid Customer Number is required': ''}
                    />
                    <TextField
                        label='Description'
                        multiline
                        rows={4}
                        {...register('description')}
                    />
                    
                    <Button variant='contained' color='primary' type='submit' style={{backgroundColor: '#76b900'}}>
                        Generate URL
                    </Button>
                    <Button variant='outlined' color= 'secondary' onClick={handleReset}>
                        Reset    
                    </Button> 
                </Box>
        </Paper>
    </Grid>
    <Grid item xs={12} md={5}>
    {alertMessage && (
            <Alert severity={alertType}>
                {alertMessage}
            </Alert>
        )}
        <Paper sx={{ padding: 2}}>
            <Typography variant='h6'>Generated URL</Typography>
            <TextField
                label='URL'
                value={generatedUrl}
                multiline
                rows={4}
                fullWidth
                InputProps= {{
                    readOnly: true
                }}
                variant='outlined'
                />
                {generatedUrl && (
                    <Box sx={{ display: 'flex', flexDirection:'column', alignItems: 'center', gap: 2, marginTop: 2 }}>
                    <FileUploadComponent
                        onFileSelect={setSelectedFile}
                        selectedFile={selectedFile}
                        onFileRemove={() => setSelectedFile(null)}
                    />
                    <Box sx={{ display:'flex', gap: 2}}>
                        <Button
                            variant='contained'
                            color='secondary'
                            style={{backgroundColor: '#76b900'}}
                            onClick={() => navigator.clipboard.writeText(generatedUrl)}
                            startIcon={<ContentCopyIcon />}
                            sx={{ marginTop: 2}}
                        >
                            Copy URL
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            startIcon={<EmailIcon />}
                            style={{backgroundColor: '#76b900'}}
                            sx={{ marginTop: 2}}
                            onClick={handleSendEmail}
                            //Email logic
                        >
                            Send Email
                        </Button>
                    </Box>
                </Box>
                )}
        </Paper>
    </Grid>
    </Grid>        
</Container>
    );
};

export default CreatePaymentForm;